import React, { useEffect, useMemo } from "react";

import Layout from "src/components/layout/dashboard/Layout";
import Card from "src/components/common/card/Card";
import PageHeader from "src/components/common/PageHeader";
import Form, { FormField } from "src/components/ui/Form";

import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import { useNavigate, useParams } from "react-router-dom";
import * as planActions from "src/redux/slices/planSlice";
import { toast } from "react-toastify";
import { PlanFeatures } from "src/types/plan.types";

const PlanForm: React.FC = () => {
  const { planId } = useParams();

  const navigate = useNavigate();

  const dispatch = useAppDispatch();
  const { createPlan, editPlan, planDetails } = useAppSelector(
    (state) => state.plan
  );

  const defaultPlanFeatures: PlanFeatures = useMemo(
    () => ({
      totalUsers: 1,
      totalProducts: 10,
      totalTransactions: 10,
      totalSaleOrders: 10,
      totalPurchaseOrders: 10,
      totalSuppliers: 1,
    }),
    []
  );

  // ✅ Define all form fields
  const fields: FormField[] = useMemo(
    () => [
      {
        label: "Plan Name",
        name: "planName",
        type: "text",
        placeholder: "Enter plan name",
        validations: { required: true },
      },
      {
        label: "Plan Code",
        name: "planCode",
        type: "text",
        placeholder: "Enter plan code",
        validations: { required: true },
      },
      {
        label: "Plan Description",
        name: "description",
        type: "textarea",
        placeholder: "Enter Plan description",
        validations: { required: true },
      },
      {
        label: "Plan Price",
        name: "price",
        type: "number",
        placeholder: "Enter Plan Price",
        validations: { required: true },
      },
      {
        label: "Plan Duration(In Days)",
        name: "durationInDays",
        type: "number",
        placeholder: "Enter Plan Duration In Days",
        validations: { required: true },
      },
      {
        label: "Total Users",
        name: "totalUsers",
        type: "number",
        placeholder: "Enter total users",
        defaultValue: defaultPlanFeatures.totalUsers,
        validations: { required: true },
      },
      {
        label: "Total Products",
        name: "totalProducts",
        type: "number",
        placeholder: "Enter total products",
        defaultValue: defaultPlanFeatures.totalProducts,
        validations: { required: true },
      },
      {
        label: "Total Transactions",
        name: "totalTransactions",
        type: "number",
        placeholder: "Enter total transactions",
        defaultValue: defaultPlanFeatures.totalTransactions,
        validations: { required: true },
      },
      {
        label: "Total Sale Orders",
        name: "totalSaleOrders",
        type: "number",
        placeholder: "Enter total sale orders",
        defaultValue: defaultPlanFeatures.totalSaleOrders,
        validations: { required: true },
      },
      {
        label: "Total Purchase Orders",
        name: "totalPurchaseOrders",
        type: "number",
        placeholder: "Enter total purchase orders",
        defaultValue: defaultPlanFeatures.totalPurchaseOrders,
        validations: { required: true },
      },
      {
        label: "Total Suppliers",
        name: "totalSuppliers",
        type: "number",
        placeholder: "Enter total suppliers",
        defaultValue: defaultPlanFeatures.totalSuppliers,
        validations: { required: true },
      },
    ],
    [defaultPlanFeatures]
  );

  const initialState = useMemo(() => {
    const features = planDetails.data?.features || defaultPlanFeatures;
    return {
      ...planDetails.data,
      totalUsers: features?.totalUsers ?? defaultPlanFeatures.totalUsers,
      totalProducts:
        features?.totalProducts ?? defaultPlanFeatures.totalProducts,
      totalTransactions:
        features?.totalTransactions ?? defaultPlanFeatures.totalTransactions,
      totalSaleOrders:
        features?.totalSaleOrders ?? defaultPlanFeatures.totalSaleOrders,
      totalPurchaseOrders:
        features?.totalPurchaseOrders ??
        defaultPlanFeatures.totalPurchaseOrders,
      totalSuppliers:
        features?.totalSuppliers ?? defaultPlanFeatures.totalSuppliers,
    };
  }, [planDetails.data, defaultPlanFeatures]);

  useEffect(() => {
    if (planId) {
      dispatch(planActions.getPlanDetailsRequest(planId));
    } else {
      dispatch(planActions.resetPlanDetails());
    }
  }, [dispatch, planId]);

  useEffect(() => {
    if (planDetails.error) {
      toast.error("Failed to fetch plan details");
    }
  }, [planDetails.error]);

  // ✅ Success/Error side-effects
  useEffect(() => {
    if (createPlan.data) {
      toast.success("Plan created successfully");
      handleClose();
    } else if (createPlan.error) {
      toast.error(`Error: ${createPlan.error}`);
    }
  }, [createPlan]);

  // ✅ Success/Error side-effects
  useEffect(() => {
    if (editPlan.data) {
      toast.success("Plan modified successfully");
      handleClose();
    } else if (editPlan.error) {
      toast.error(`Error: ${editPlan.error}`);
    }
  }, [editPlan]);

  // handleClose
  const handleClose = () => {
    dispatch(planActions.resetCreatePlan());
    dispatch(planActions.resetEditPlan());
    navigate("/admin/plans/list");
  };

  // ✅ Form submission handler
  const handleSubmit = (data: Record<string, any>) => {
    const {
      totalUsers,
      totalProducts,
      totalTransactions,
      totalSaleOrders,
      totalPurchaseOrders,
      totalSuppliers,
      ...rest
    } = data;
    const features: PlanFeatures = {
      totalUsers: Number(totalUsers),
      totalProducts: Number(totalProducts),
      totalTransactions: Number(totalTransactions),
      totalSaleOrders: Number(totalSaleOrders),
      totalPurchaseOrders: Number(totalPurchaseOrders),
      totalSuppliers: Number(totalSuppliers),
    };
    if (planDetails?.data?._id) {
      dispatch(
        planActions.editPlanRequest({
          ...(rest as any),
          features,
          _id: planDetails.data._id,
        })
      );
    } else {
      dispatch(planActions.createPlanRequest({ ...rest, features }));
    }
  };

  return (
    <Layout
      type="admin"
      meta={{
        title: planId ? "Edit Plan | Invyntra" : "Create Plan | Invyntra",
        description: "Create or edit subscription plans.",
        keywords: "plans, pricing, invyntra",
      }}
    >
      <div className="space-y-4">
        <PageHeader
          title={planId ? "Edit Plan" : "Create Plan"}
          showBack={!!planId}
          breadcrumbData={[
            { label: "Home", path: "/admin/dashboard" },
            { label: "Plans", path: "/admin/plans/list" },
            { label: planId ? "Edit Plan" : "Create Plan", path: "" },
          ]}
        />
        <Card>
          <Form
            initialState={initialState as any}
            fields={fields}
            onSubmit={handleSubmit}
            onCancel={() => navigate("/admin/plans/list")}
            loading={
              createPlan.loading || editPlan.loading || planDetails.loading
            }
          />
        </Card>
      </div>
    </Layout>
  );
};

export default PlanForm;
